import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MATCH_RULES } from '@chromarble/game-core';
import { type RealtimeEvent, type RoomDto, roomTopic } from '@chromarble/shared-types';
import { roomTypeLabel } from '@chromarble/crew-adapter';
import { ConnectionBadge, ErrorNote, Screen, ShareSheet } from '../components/ui';
import { useRealtimeStatus, useRealtimeTopic } from '../hooks/realtime';
import { api } from '../services/api';
import { useSession } from '../services/session';

const STATUS_TEXT: Record<string, string> = {
  WAITING: '참가자 준비를 기다리는 중',
  READY: '모두 준비 완료 — 방장이 시작할 수 있습니다',
  COUNTDOWN: '경기 시작!',
  PLAYING: '경기 진행 중',
  FINISHING: '결과 집계 중',
  FINISHED: '경기 종료',
  CANCELLED: '닫힌 방',
  EXPIRED: '만료된 방',
};

export default function LobbyPage() {
  const { roomId = '' } = useParams();
  const navigate = useNavigate();
  const user = useSession((s) => s.user)!;
  const [room, setRoom] = useState<RoomDto | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const rtStatus = useRealtimeStatus();

  const load = useCallback(async () => {
    try {
      setRoom((await api.room(roomId)).room);
    } catch (e) {
      setError(e);
    }
  }, [roomId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Fallback polling when realtime is unavailable.
  useEffect(() => {
    const t = setInterval(() => void load(), rtStatus === 'open' ? 15_000 : 3_000);
    return () => clearInterval(t);
  }, [load, rtStatus]);

  const presences = useRealtimeTopic(
    room ? roomTopic(room.id) : null,
    (e: RealtimeEvent) => {
      if ('room' in e) setRoom(e.room);
      else if (e.type === 'MATCH_STARTING') navigate(`/match/${e.matchId}`);
      else if (e.type === 'ROOM_CLOSED' || e.type === 'ROOM_REMATCH') void load();
    },
    // A start that happened before the subscription was active is picked up by this reload.
    { trackPresence: true, onSubscribed: () => void load() },
  );
  const online = new Set(presences.map((p) => p.userId));

  // Late arrivals (or missed events): jump into a running match.
  useEffect(() => {
    if (room?.currentMatchId && (room.status === 'COUNTDOWN' || room.status === 'PLAYING')) navigate(`/match/${room.currentMatchId}`);
  }, [room, navigate]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  if (!room) {
    return (
      <Screen title="게임방" back="/">
        {error ? <ErrorNote error={error} /> : <p className="muted">불러오는 중…</p>}
      </Screen>
    );
  }

  const me = room.members.find((m) => m.userId === user.id);
  const isHost = room.hostUserId === user.id;
  const lobbyOpen = room.status === 'WAITING' || room.status === 'READY';
  const isTiebreak = room.rematchKind === 'TIEBREAK_LAST' || room.rematchKind === 'TIEBREAK_TOP';

  return (
    <Screen title={room.title} back="/" actions={<ConnectionBadge />}>
      <section className="card" data-testid="lobby">
        <p className="muted small">
          {roomTypeLabel(room.roomType)}
          {room.roomType === 'LUNCH_BET' ? ' · 최하위 점심결제' : ''} · {MATCH_RULES.durationMs / 1000}초 · 재대전 {room.allowRematch ? '허용' : '불가'}
          {isTiebreak ? ' · 동점자 재대전' : ''}
        </p>
        <p data-testid="room-status" data-status={room.status}>
          <strong>{STATUS_TEXT[room.status] ?? room.status}</strong>
        </p>
        <h3>
          참가자 {room.members.length}/{room.maxPlayers}
        </h3>
        <ul className="members" data-testid="members">
          {room.members.map((m) => (
            <li key={m.userId} data-testid="member" data-ready={m.isReady}>
              <span className={`presence ${online.has(m.userId) ? 'on' : ''}`} title={online.has(m.userId) ? '접속 중' : '미접속'} />
              <span className="name">
                {m.nickname}
                {m.isHost && <span className="badge">방장</span>}
                {m.userId === user.id && <span className="badge me">나</span>}
              </span>
              <span className={`ready ${m.isReady ? 'on' : ''}`}>{m.isReady ? '준비 완료' : '대기'}</span>
            </li>
          ))}
        </ul>
      </section>

      {me && lobbyOpen && (
        <div className="stack">
          <button className={`btn ${me.isReady ? 'secondary' : ''}`} disabled={busy} onClick={() => act(async () => setRoom((await api.ready(room.id, !me.isReady)).room))} data-testid="ready-toggle">
            {me.isReady ? '준비 취소' : '준비 완료'}
          </button>
          {isHost && (
            <button
              className="btn"
              disabled={busy || room.status !== 'READY'}
              onClick={() =>
                act(async () => {
                  const r = await api.start(room.id);
                  navigate(`/match/${r.match.id}`);
                })
              }
              data-testid="start-match"
            >
              경기 시작
            </button>
          )}
          {isHost && room.status !== 'READY' && <p className="muted small">2명 이상이 모두 준비 완료하면 시작할 수 있습니다.</p>}
        </div>
      )}

      {room.status === 'FINISHED' && room.currentMatchId && (
        <button className="btn secondary" onClick={() => navigate(`/matches/${room.currentMatchId}/result`)}>
          지난 경기 결과 보기
        </button>
      )}

      {lobbyOpen && !isTiebreak && room.inviteUrl && (
        <ShareSheet load={() => api.shareRoom(room.id)} scopeNote="링크를 받은 같은 Crew 구성원만 참가할 수 있고, 방이 만료되면 링크도 사용할 수 없습니다." />
      )}

      <ErrorNote error={error} />
      <button
        className="link-btn"
        onClick={() =>
          act(async () => {
            await api.leave(room.id);
            navigate('/', { replace: true });
          })
        }
        data-testid="leave-room"
      >
        방 나가기
      </button>
    </Screen>
  );
}
